// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Checkbox,
  Typography,
  FormControlLabel,
  MenuItem,
  ListItemText,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useTheme } from "@mui/material";

// ** Third Party Imports
import dayjs from "dayjs";

// ** Icons Imports
import Icon from "components/icon";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

// ** Styled Component
import CustomTextField from "components/mui/text-field";

// ** Logic Imports
import { useDispatchBoardPermission } from "screens/DispatchBoard/permissions/DispatchBoardPermissions";
import { GetService } from "screens/Services/logic/GetService";
import { GetIndustries } from "screens/Settings/logic/GetIndustries";
import { useNavigate } from "react-router-dom";

const Topbar = (props) => {
  const navigate = useNavigate();
  //#region Permissions
  const { DispatchBoardPermissionProps } = useDispatchBoardPermission();
  const {
    hasDbGetPermission,
    hasDbSavePermission
  } = DispatchBoardPermissionProps;
  //#endregion Permissions

  const {
    store,
    mdAbove,
    dispatch,
    calendarApi,
    calendarsColor,
    leftSidebarOpen,
    leftSidebarWidth,
    handleSelectEvent,
    handleAllCalendars,
    handleCalendarsUpdate,
    handleLeftSidebarToggle,
    handleAddEventSidebarToggle,
    calendarSelectedDate,
    setCalendarSelectedDate,
    appliedFilters,
    setAppliedFilters,
  } = props;
  
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const colorsArr = calendarsColor ? Object.entries(calendarsColor) : [];

  const renderFilters = colorsArr.length
    ? colorsArr.map(([key, value]) => {
        return (
          <FormControlLabel
            key={key}
            label={key}
            sx={{ "& .MuiFormControlLabel-label": { color: "text.secondary" } }}
            control={
              <Checkbox
                color={value}
                checked={store.selectedCalendars.includes(key)}
                onChange={() => dispatch(handleCalendarsUpdate(key))}
              />
            }
          />
        );
      })
    : null;

  // State for current date
  const [currentDate, setCurrentDate] = useState(dayjs());
  
  // State for filter dialog
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  
  // State for dropdown options
  const [serviceOptions, setServiceOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  
  // Status options from enum
  const statusOptions = [
    { value: 'Unscheduled', label: 'Unscheduled' },
    { value: 'Scheduled', label: 'Scheduled' },
    { value: 'Awaiting Technician', label: 'Awaiting Technician' },
    { value: 'Awaiting Asset', label: 'Awaiting Asset' },
    { value: 'Awaiting Material', label: 'Awaiting Material' },
    { value: 'Rescheduled', label: 'Rescheduled' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'On Hold', label: 'On Hold' },
    { value: 'Canceled', label: 'Canceled' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Incomplete', label: 'Incomplete' },
    { value: 'Ongoing', label: 'Ongoing' },
    { value: 'Phone Scheduled', label: 'Phone Scheduled' },
    { value: 'On-Site Scheduled', label: 'On-Site Scheduled' }
  ];
  
  // Update current date when calendar changes
  useEffect(() => {
    if (calendarApi) {
      const calDate = calendarApi.getDate();
      setCurrentDate(dayjs(calDate));
    }
  }, [calendarApi]);
  
  // Fetch services and industries when dialog opens
  useEffect(() => {
    const fetchFilterData = async () => {
      if (filterDialogOpen && hasDbGetPermission) {
        setFiltersLoading(true);
        try {
          // Fetch Service Types from GetService (same as ServiceandBundles report)
          const serviceResponse = await GetService(0, 100000, 1, '', navigate);
          if (serviceResponse && !serviceResponse.isError && serviceResponse.result) {
            const uniqueServices = [...new Set(serviceResponse.result.map(item => item.serviceOfferingName))]
              .filter(service => service) // Remove null/undefined
              .sort()
              .map(service => ({ value: service, label: service }));
            setServiceOptions(uniqueServices);
          }
          
          // Fetch industries
          const industriesData = await GetIndustries(navigate);
          if (industriesData && Array.isArray(industriesData)) {
            const industryList = industriesData
              .map(industry => ({
                value: industry.industryName,  // Changed to use name instead of ID
                label: industry.industryName
              }))
              .filter(industry => industry.label) // Remove null/undefined
              .sort((a, b) => a.label.localeCompare(b.label));
            setIndustryOptions(industryList);
          }
        } catch (error) {
          console.error("Error fetching filter data:", error);
        } finally {
          setFiltersLoading(false);
        }
      }
    };
    
    fetchFilterData();
  }, [filterDialogOpen, hasDbGetPermission, navigate]);

  const handleSidebarToggleSidebar = () => {
    handleAddEventSidebarToggle();
    dispatch(handleSelectEvent(null));
  };

  // Filter dialog handlers
  const handleOpenFilterDialog = () => {
    setFilterDialogOpen(true);
  };

  const handleCloseFilterDialog = () => {
    // Reset filters to empty when closing without applying
    setSelectedStatuses([]);
    setSelectedServices([]);
    setSelectedIndustries([]);
    setFilterDialogOpen(false);
  };

  // Handle filter changes with "All" support
  const handleStatusChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedStatuses.length === statusOptions.length) {
        setSelectedStatuses([]);
      } else {
        setSelectedStatuses(statusOptions.map(option => option.value));
      }
    } else {
      setSelectedStatuses(value);
    }
  };

  const handleServiceChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedServices.length === serviceOptions.length) {
        setSelectedServices([]);
      } else {
        setSelectedServices(serviceOptions.map(option => option.value));
      }
    } else {
      setSelectedServices(value);
    }
  };

  const handleIndustryChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedIndustries.length === industryOptions.length) {
        setSelectedIndustries([]);
      } else {
        setSelectedIndustries(industryOptions.map(option => option.value));
      }
    } else {
      setSelectedIndustries(value);
    }
  };

  const handleResetFilters = () => {
    setSelectedStatuses([]);
    setSelectedServices([]);
    setSelectedIndustries([]);
  };

  const handleApplyFilters = () => {
    // Update the applied filters state
    if (setAppliedFilters) {
      setAppliedFilters({
        statuses: selectedStatuses,
        services: selectedServices,
        industries: selectedIndustries
      });
    }

    setFilterDialogOpen(false);
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (calendarApi) {
      calendarApi.prev();
      const newDate = calendarApi.getDate();
      setCurrentDate(dayjs(newDate));
      // Update the shared date state for JobList filtering
      if (setCalendarSelectedDate) {
        setCalendarSelectedDate(newDate);
      }
    }
  };

  const handleNext = () => {
    if (calendarApi) {
      calendarApi.next();
      const newDate = calendarApi.getDate();
      setCurrentDate(dayjs(newDate));
      // Update the shared date state for JobList filtering
      if (setCalendarSelectedDate) {
        setCalendarSelectedDate(newDate);
      }
    }
  };

  const handleToday = () => {
    if (calendarApi) {
      calendarApi.today();
      const today = new Date();
      setCurrentDate(dayjs(today));
      // Update the shared date state for JobList filtering
      if (setCalendarSelectedDate) {
        setCalendarSelectedDate(today);
      }
    }
  };

  const handleDateChange = (newDate) => {
    if (calendarApi && newDate) {
      calendarApi.gotoDate(newDate.toDate());
      setCurrentDate(newDate);
      // Update the shared date state for JobList filtering
      if (setCalendarSelectedDate) {
        setCalendarSelectedDate(newDate.toDate());
      }
    }
  };

  // Format date display based on calendar view
  const getDateDisplay = () => {
    if (!calendarApi) return "";
    
    const view = calendarApi.view;
    const date = currentDate;

    switch (view.type) {
      case "dayGridMonth":
        return date.format("MMMM YYYY");
      case "timeGridWeek":
        const startOfWeek = date.startOf("week");
        const endOfWeek = date.endOf("week");
        if (startOfWeek.month() === endOfWeek.month()) {
          return `${startOfWeek.format("MMM D")} - ${endOfWeek.format("D, YYYY")}`;
        } else {
          return `${startOfWeek.format("MMM D")} - ${endOfWeek.format("MMM D, YYYY")}`;
        }
      case "timeGridDay":
        return date.format("dddd, MMM DD");
      default:
        return date.format("MMMM YYYY");
    }
  };

  if (renderFilters) {
    return (
      <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          justifyContent: "space-between",
            alignItems: isSmallScreen ? "stretch" : "center",
          mb: 4,
          gap: isSmallScreen ? 2 : 0,
        }}
      >
        {/* Left side - Navigation Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Previous Button */}
          <IconButton
            onClick={handlePrevious}
            disabled={!hasDbGetPermission}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              width: 40,
              height: 40,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {/* Date Display with Dropdown */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
              value={currentDate}
              onChange={handleDateChange}
                disabled={!hasDbGetPermission}
                slotProps={{
                  textField: { 
                    size: "small", 
                    disabled: !hasDbGetPermission,
                  sx: {
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                    },
                    "& .MuiInputBase-input": {
                      fontWeight: 500,
                    },
                  },
                  InputProps: {
                    startAdornment: null,
                  },
                },
              }}
              format="ddd, MMM DD"
              />
            </LocalizationProvider>

          {/* Next Button */}
          <IconButton
            onClick={handleNext}
              disabled={!hasDbGetPermission}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              width: 40,
              height: 40,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          {/* Today Button */}
          <Button
            variant="outlined"
            onClick={handleToday}
            disabled={!hasDbGetPermission}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Today
          </Button>
          </Box>

        {/* Right side - Filter and Quick Job Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
          width: isSmallScreen ? "100%" : "auto",
            alignSelf: isSmallScreen ? "stretch" : "center",
          }}
        >
          {/* Filter Button */}
          <Button
            variant="outlined"
            onClick={handleOpenFilterDialog}
            disabled={!hasDbGetPermission}
            startIcon={<FilterListIcon />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Filter
          </Button>

          {/* Quick Job Button */}
          <Button
            fullWidth={isSmallScreen}
            variant="contained"
            color="main"
            disabled={!hasDbSavePermission}
            {...(hasDbSavePermission && {
              onClick: handleSidebarToggleSidebar,
            })}
            startIcon={<AddIcon />}
            sx={{
              whiteSpace: "nowrap",
              borderRadius: 1,
            }}
          >
            Quick Job
          </Button>
        </Box>
      </Box>

        {/* Filter Dialog */}
        <Dialog
        open={filterDialogOpen}
        onClose={handleCloseFilterDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
          }}
        >
          <Typography variant="h4">
            Filter
          </Typography>
          <IconButton
            onClick={handleCloseFilterDialog}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {filtersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 3,
              }}
            >
              {/* Status Dropdown */}
              <Box>
                <CustomTextField
                  select
                  size="small"
                  label="Status"
                  value={selectedStatuses}
                  onChange={handleStatusChange}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected || selected.length === 0) {
                        return "Select Status";
                      }
                      if (selected.length === statusOptions.length) {
                        return "All Selected";
                      }
                      if (selected.length === 1) {
                        return selected[0];
                      }
                      return `${selected.length} statuses selected`;
                    },
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 220,
                          width: 300,
                        }
                      }
                    }
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      width: "100%",
                      border: "1px solid #D9D9D9",
                      boxShadow: "none",
                      backgroundColor: theme.palette.mode === "dark" ? "#1B2431" : "#fff !important",
                    },
                  }}
                >
                  <MenuItem key="all" value="all">
                    <Checkbox checked={selectedStatuses.length === statusOptions.length} />
                    <ListItemText 
                      primary="All" 
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                        }
                      }}
                    />
                  </MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      <Checkbox checked={selectedStatuses.includes(status.value)} />
                      <ListItemText 
                        primary={status.label} 
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Box>

              {/* Service Dropdown */}
              <Box>
                <CustomTextField
                  select
                  size="small"
                  label="Service"
                  value={selectedServices}
                  onChange={handleServiceChange}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected || selected.length === 0) {
                        return "Select Service";
                      }
                      if (selected.length === serviceOptions.length) {
                        return "All Selected";
                      }
                      if (selected.length === 1) {
                        return selected[0];
                      }
                      return `${selected.length} services selected`;
                    },
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 220,
                          width: 300,
                        }
                      }
                    }
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      width: "100%",
                      border: "1px solid #D9D9D9",
                      boxShadow: "none",
                      backgroundColor: theme.palette.mode === "dark" ? "#1B2431" : "#fff !important",
                    },
                  }}
                >
                  <MenuItem key="all" value="all">
                    <Checkbox checked={selectedServices.length === serviceOptions.length} />
                    <ListItemText 
                      primary="All" 
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                        }
                      }}
                    />
                  </MenuItem>
                  {serviceOptions.map((service) => (
                    <MenuItem key={service.value} value={service.value}>
                      <Checkbox checked={selectedServices.includes(service.value)} />
                      <ListItemText 
                        primary={service.label} 
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Box>

              {/* Industry Dropdown */}
              <Box>
                <CustomTextField
                  select
                  size="small"
                  label="Industry"
                  value={selectedIndustries}
                  onChange={handleIndustryChange}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected || selected.length === 0) {
                        return "Select Industry";
                      }
                      if (selected.length === industryOptions.length) {
                        return "All Selected";
                      }
                      if (selected.length === 1) {
                        const industry = industryOptions.find(i => i.value === selected[0]);
                        return industry ? industry.label : selected[0];
                      }
                      return `${selected.length} industries selected`;
                    },
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 220,
                          width: 300,
                        }
                      }
                    }
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      width: "100%",
                      border: "1px solid #D9D9D9",
                      boxShadow: "none",
                      backgroundColor: theme.palette.mode === "dark" ? "#1B2431" : "#fff !important",
                    },
                  }}
                >
                  <MenuItem key="all" value="all">
                    <Checkbox checked={selectedIndustries.length === industryOptions.length} />
                    <ListItemText 
                      primary="All" 
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                        }
                      }}
                    />
                  </MenuItem>
                  {industryOptions.map((industry) => (
                    <MenuItem key={industry.value} value={industry.value}>
                      <Checkbox checked={selectedIndustries.includes(industry.value)} />
                      <ListItemText 
                        primary={industry.label} 
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Box>
            </Box>
          )}
        </DialogContent>

           <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: "space-between", // Changed from "flex-end" to "space-between"
          }}
        >
          {/* Cancel Button at flex start */}
          <Button
            onClick={handleCloseFilterDialog}
            color="info"
            variant="contained"
          >
            Cancel
          </Button>
          
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              onClick={handleResetFilters}
              sx={{
                textTransform: "none",
                color: theme.palette.text.secondary,
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="main"
              onClick={handleApplyFilters}
              sx={{
                textTransform: "none",
                px: 4,
              }}
            >
              Apply
            </Button>
          </Box>
        </DialogActions>
        </Dialog>
      </>
    );
  } else {
    return null;
  }
};

export default Topbar;